import { Icon } from "assets/Icon";
import { cn } from "utils/helpers";
import React, { ButtonHTMLAttributes, ReactNode } from "react";
import tw from "twin.macro";
import styled, { css } from "styled-components";
import { IconName } from "assets/ImportedIcons";

type Size = "sm" | "md" | "lg" | "xl" | "xxl";
type Variant = "gray" | "color" | "default";

interface TextButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
	children: ReactNode;
	className?: string;
	icon?: IconName;
	size?: Size;
	variant?: Variant;
}

const sizes: Record<Size, any> = {
	sm: tw`text-sm gap-1.5`,
	md: tw`text-sm gap-1.5`,
	lg: tw`text-base gap-2`,
	xl: tw`text-base gap-2`,
	xxl: tw`text-lg gap-3`,
};

const variants: Record<Exclude<Variant, "default">, any> = {
	gray: tw`text-tertiary-700 border-tertiary-700`,
	color: tw`text-utility-brand-700 border-utility-brand-700`,
};

const ButtonStyled = styled.button<{ size?: Size; variant?: Variant }>(
	({ size = "md", variant = "default" }) => [
		sizes[size],
		variant !== "default" ? variants[variant as keyof typeof variants] : null,
		tw`flex justify-center items-center gap-x-1.5 font-semibold transition-all disabled:pointer-events-none disabled:opacity-50`,
		css`
			svg {
				${size !== "xxl" ? tw`w-5 h-5` : tw`w-6 h-6`}
			}
		`,
	]
);

const LineStyled = styled.span<{ variant?: Variant }>(
	({ variant = "default" }) => [
		tw`left-0 absolute w-0 mt-6 group-hover:w-full transition-all h-[1px]`,
		variant === "default" ? tw`bg-tertiary-400` : tw`bg-utility-brand-400`,
	]
);

const TextButton: React.FC<TextButtonProps> = ({
	icon,
	children,
	type = "button",
	className,
	size = "md",
	variant = "default",
	...props
}) => {
	return (
		<ButtonStyled
			className={cn("group relative", className)}
			type={type}
			size={size}
			variant={variant}
			{...props}
		>
			{icon && <Icon icon={icon} />}
			{children}
			<LineStyled variant={variant} />
		</ButtonStyled>
	);
};

export default TextButton;
